import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { UnitOfMeasureTypes, UomText } from 'src/common/enumerations/unit-of-measure-type';
import { convertCurrency } from 'src/common/helpers/currency-helper';
import { truncateNumberWithPrecision } from 'src/common/helpers/number-helper';
import { TbrData } from 'src/common/interfaces/states/IElectronicJournalRecord';
import { store, AppState } from 'src/common/redux/configure-store';
import { CartItemViewData } from 'src/items-list/business-logic/service/interfaces/cart-response';
import { ReturnsType } from 'src/transaction-search/interfaces/get-effective-return-policy-response';
import { ItemTypes } from '../../common/enumerations/rti-enums/item-types';
import { getTotalDepositPrice } from '../helper/tbr-item-total-price-helper';
import ActionWindowLinkedLine from './linked-line';

const ActionWindowLinkedLines: React.FC<Props> = (props: Props): JSX.Element => {
  const { linkedLine, parentLine, atomicMsuUnit, isSelectedItemReturnItem, tbrData } = props;
  const { t } = useTranslation();
  const itemType: string | undefined = linkedLine.parentLineLinkType ?? linkedLine.itemType;

  const isTBRActive =
    store.getState().posMode.returnMode.returnType === ReturnsType.TransactionBasedReturn;
  const itemState = useSelector((state: AppState) => state.itemList);
  const tbrState = useSelector((state: AppState) => state.tbr);
  const ejItems = isTBRActive ? tbrState.tbrItemsList : itemState.ejItems;
  const selectedItem = ejItems.find(item => item.Selected);
  const isTareOverride = !!selectedItem?.TareWeight?.IsTareOverride;
  const isReturnType = parentLine.status === 'Returned' || isSelectedItemReturnItem;

  let linkedItemWeightText: string = '';
  let linkedLineText: string = '';
  let linkedLinePrice: string = '';
  let linkedLineUnitPrice: string = '';
  let baseQuantifier: string = '';
  let tare: string = '';
  let unit = atomicMsuUnit;
  const amount = linkedLine.amount ?? linkedLine.extendedAmount;

  switch (itemType?.toUpperCase()) {
    case ItemTypes.Tare.toUpperCase():
      tare = truncateNumberWithPrecision(
        parentLine.lineItem?.quantity?.weightQuantity?.tare?.tareValue || 0,
        false,
      );
      unit = amount === 0 ? atomicMsuUnit : UomText[UnitOfMeasureTypes.Each];
      linkedItemWeightText =
        amount !== 0
          ? '1'
          : `${tare} ${
              UomText[parentLine.lineItem?.quantity?.unitOfMeasure ?? UnitOfMeasureTypes.Each]
            }`;
      if (isTareOverride) {
        linkedLineText = amount === 0 ? ' Tare' : ` ${linkedLine.description}`;
        linkedLinePrice = amount === 0 ? '' : `${convertCurrency(amount ?? 0)}`;
        linkedLineUnitPrice = `${convertCurrency(
          (linkedLine.regularPrice ?? linkedLine.price)?.unitPrice ?? 0,
        )}`;
      }

      baseQuantifier = amount === 0 ? ` ${t('TareNet')} ` : ` ${UomText[UnitOfMeasureTypes.Each]} `;
      break;
    case ItemTypes.Deposit.toUpperCase():
      linkedLineText = `${linkedLine.description}`;
      linkedItemWeightText = tbrData?.tbrAvailableQty
        ? `${tbrData?.tbrAvailableQty}`
        : `${linkedLine.regularPrice?.quantity ?? linkedLine.lineItem?.quantity?.value} `;
      linkedLineUnitPrice = `${convertCurrency(
        (linkedLine.regularPrice ?? linkedLine.price)?.unitPrice ?? 0,
      )}`;
      linkedLinePrice = `${convertCurrency(
        tbrData ? getTotalDepositPrice(parentLine, tbrData) : amount ?? 0,
      )}`;
      unit = ` ${UomText[UnitOfMeasureTypes.Each]} `;
      break;
    case ItemTypes.Warranty.toUpperCase():
      linkedLineText = `${linkedLine.description}`;
      linkedItemWeightText = `${linkedLine.lineItem?.quantity?.value} `;
      linkedLineUnitPrice = `${convertCurrency(
        (linkedLine.regularPrice ?? linkedLine.price)?.unitPrice ?? 0,
      )}`;
      linkedLinePrice = `${convertCurrency(amount ?? 0)}`;
      baseQuantifier = ` ${UomText[UnitOfMeasureTypes.Each]} `;
      break;
    // ToDo: Handle other linked lines
  }

  linkedLinePrice = linkedLinePrice === '$0' ? '$0.00' : linkedLinePrice;

  if (isReturnType) {
    linkedLinePrice = `-${linkedLinePrice}`;
  }

  return (
    <ActionWindowLinkedLine
      linkedItemWeightText={linkedItemWeightText}
      linkedLineText={linkedLineText}
      linkedLinePrice={linkedLinePrice}
      linkedLineUnitPrice={linkedLineUnitPrice}
      baseQuantifier={baseQuantifier}
      atomicMsuUnit={unit}
      linkedItemType={itemType}
    />
  );
};

interface Props {
  linkedLine: CartItemViewData;
  parentLine: CartItemViewData;
  isSelectedItemReturnItem?: boolean;
  atomicMsuUnit: string | undefined;
  tbrData?: TbrData;
}

export default ActionWindowLinkedLines;
